import { RowData } from "../../../s25-spreadsheet/types/sheet.js.types.expanded";
import { ContactImportRow, ImportCustomAttribute } from "../../import.service";
import { ImportUtil } from "../import.util";

/**
 * Parses an uploaded contact spreadsheet into {@link ContactImportRow}s.
 *
 * One flattened sheet per import (no separate tabs): each row carries a contact's demographic data,
 * an optional 25Live user (username/password/security group), and any number of custom attribute
 * columns. Standard headers (email, names, internal id, contact id, address block, title, and the
 * user columns) map to contact/user fields. Any header beginning with the {@code "CA: "} prefix is
 * treated as a contact custom attribute column: the prefix is stripped, an optional leading
 * {@code <TYPE>} token captures the data type for auto-creation, and the remaining text is the
 * attribute name. One custom attribute per column.
 *
 *   "CA: <B> Veteran"  -> { name: "Veteran", dataType: "B" }   (auto-create as Boolean if missing)
 *   "CA: Veteran"      -> { name: "Veteran" }                  (must already exist by name)
 *
 * Security group names are left as {@code securityGroupName}; the component resolves them to ids
 * (groups are never auto-created).
 */
type ContactField =
    | "email"
    | "firstName"
    | "familyName"
    | "additionalNames"
    | "namePrefix"
    | "nameSuffix"
    | "internalId"
    | "contTitle"
    | "address"
    | "city"
    | "stateProv"
    | "zipPostCode"
    | "country"
    | "phone"
    | "fax"
    | "contId"
    | "r25Username"
    | "r25Password"
    | "securityGroupName";

export class ContactSpreadsheetUtil {
    /** Header aliases per field. First match (in this order) wins. */
    private static readonly fieldAliases: ReadonlyArray<readonly [ContactField, readonly string[]]> = [
        ["email", ["email", "e_mail", "contact_email", "email_address"]],
        ["firstName", ["first_name", "firstname", "contact_first_name", "given_name"]],
        ["familyName", ["family_name", "last_name", "lastname", "contact_family_name", "surname"]],
        ["additionalNames", ["middle_name", "middlename", "additional_names", "middle_names"]],
        ["namePrefix", ["name_prefix", "nameprefix", "prefix"]],
        ["nameSuffix", ["name_suffix", "namesuffix", "suffix"]],
        ["internalId", ["internal_id", "internalid", "external_id", "sis_id", "banner_id"]],
        ["contTitle", ["title", "contact_title", "job_title"]],
        ["address", ["address", "street", "street_address"]],
        ["city", ["city"]],
        ["stateProv", ["state", "province", "state_province", "state_prov"]],
        ["zipPostCode", ["zip", "postal_code", "post_code", "zip_postal_code", "zip_code"]],
        ["country", ["country"]],
        ["phone", ["phone", "telephone", "phone_number"]],
        ["fax", ["fax"]],
        ["contId", ["contact_id", "cont_id", "contactid"]],
        ["r25Username", ["username", "user_name", "r25_username", "login"]],
        ["r25Password", ["password", "r25_password"]],
        ["securityGroupName", ["security_group", "securitygroup", "security_group_name", "group", "group_name"]],
    ];

    public static rowsToContacts(rows: RowData[], errorSet: Set<string>): { contacts: ContactImportRow[] } {
        const contacts: ContactImportRow[] = [];
        if (!rows || !rows.length) {
            return { contacts };
        }

        // Header row -> column metadata.
        const headerByCol = new Map<
            number,
            { kind: "field" | "ca"; field?: ContactField; ca?: { name: string; dataType?: string } }
        >();
        const seenCaNames = new Map<string, number>();
        const headerRow = rows[0];
        if (headerRow?.cells) {
            for (let columnIndex = 0; columnIndex < headerRow.cells.length; columnIndex++) {
                const rawHeader = headerRow.cells[columnIndex]?.text;
                if (!rawHeader || !rawHeader.trim()) {
                    continue;
                }
                const header = rawHeader.trim();
                if (ImportUtil.isCustomAttributeHeader(header)) {
                    const parsedCa = ImportUtil.parseCustomAttributeHeader(header);
                    if (parsedCa) {
                        const lowerName = parsedCa.name.toLowerCase();
                        const priorColumn = seenCaNames.get(lowerName);
                        if (priorColumn !== undefined) {
                            errorSet.add(
                                `Duplicate CA header "${parsedCa.name}" in columns ${priorColumn + 1} and ${columnIndex + 1}`,
                            );
                        } else {
                            seenCaNames.set(lowerName, columnIndex);
                            headerByCol.set(columnIndex, { kind: "ca", ca: parsedCa });
                        }
                    } else {
                        errorSet.add(`Could not parse custom attribute header: "${header}"`);
                    }
                } else {
                    const field = this.resolveField(header);
                    if (field) {
                        headerByCol.set(columnIndex, { kind: "field", field });
                    }
                }
            }
        }

        // Track duplicate/conflict QC across the whole sheet.
        const emailToRow = new Map<string, number>();
        const usernameToRow = new Map<string, number>();
        const internalIdToRow = new Map<string, number>();

        for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
            const cells = rows[rowIndex]?.cells;
            if (!cells || !cells.length) {
                continue;
            }

            const contact: ContactImportRow = { customAttributes: [] };
            let hasAnyValue = false;

            headerByCol.forEach((meta, columnIndex) => {
                const text = cells[columnIndex]?.text;
                if (text === undefined || text === null || `${text}`.trim() === "") {
                    return;
                }
                const value = `${text}`.trim();

                if (meta.kind === "field" && meta.field) {
                    hasAnyValue = true;
                    this.applyField(contact, meta.field, value);
                } else if (meta.kind === "ca" && meta.ca) {
                    hasAnyValue = true;
                    const lowerName = meta.ca.name.toLowerCase();
                    const alreadyPresent = contact.customAttributes!.some(
                        (existing) => existing.name.toLowerCase() === lowerName,
                    );
                    if (alreadyPresent) {
                        return;
                    }
                    const importCa: ImportCustomAttribute = {
                        name: meta.ca.name,
                        dataType: meta.ca.dataType,
                        value,
                    };
                    contact.customAttributes!.push(importCa);
                }
            });

            if (!hasAnyValue) {
                continue;
            }

            const rowLabel = rowIndex + 1;
            if (contact.contId === undefined && !contact.email) {
                errorSet.add(`Row ${rowLabel}: a contact requires an email (or contact_id)`);
                continue;
            }

            // A user row needs an identity to attach to, and a group needs a user.
            if (contact.securityGroupName && !contact.r25Username) {
                errorSet.add(`Row ${rowLabel}: a security group requires a username`);
                continue;
            }

            if (contact.r25Username && contact.contId === undefined && !contact.email) {
                errorSet.add(`Row ${rowLabel}: a username requires an email (or contact_id) to attach to`);
                continue;
            }

            if (contact.email) {
                const emailKey = contact.email.toLowerCase();
                const priorRow = emailToRow.get(emailKey);
                if (priorRow !== undefined) {
                    errorSet.add(`Duplicate email "${contact.email}" on rows ${priorRow} and ${rowLabel}`);
                    continue;
                }
                emailToRow.set(emailKey, rowLabel);
            }

            if (contact.r25Username) {
                const usernameKey = contact.r25Username.toLowerCase();
                const priorRow = usernameToRow.get(usernameKey);
                if (priorRow !== undefined) {
                    errorSet.add(`Duplicate username "${contact.r25Username}" on rows ${priorRow} and ${rowLabel}`);
                    continue;
                }
                usernameToRow.set(usernameKey, rowLabel);
            }

            if (contact.internalId) {
                const internalIdKey = contact.internalId.trim();
                const priorRow = internalIdToRow.get(internalIdKey);
                if (priorRow !== undefined) {
                    errorSet.add(`Duplicate Internal ID "${contact.internalId}" on rows ${priorRow} and ${rowLabel}`);
                    continue;
                }
                internalIdToRow.set(internalIdKey, rowLabel);
            }

            contacts.push(contact);
        }

        return { contacts };
    }

    private static applyField(contact: ContactImportRow, field: ContactField, value: string): void {
        if (field === "contId") {
            const parsed = parseInt(value, 10);
            if (!isNaN(parsed)) {
                contact.contId = parsed;
            }
            return;
        }
        contact[field] = value;
    }

    private static resolveField(header: string): ContactField | undefined {
        const normalized = ImportUtil.normalize(header);
        for (const [field, aliases] of this.fieldAliases) {
            if (aliases.some((alias) => ImportUtil.normalize(alias) === normalized)) {
                return field;
            }
        }
        return undefined;
    }
}
